import { Component, inject } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { ApplicationItem } from '../../models/application.interface';

@Component({
  selector: 'app-application-detail-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatChipsModule],
  template: `
    <div class="detail-header">
      <h2 mat-dialog-title>Детали заявки</h2>
      <button mat-icon-button (click)="close()"><mat-icon>close</mat-icon></button>
    </div>

    <mat-dialog-content class="detail-content">

      <!-- Статус -->
      <div class="status-row">
        <span class="status-badge" [class]="'status-' + app.applicationStatus.toLowerCase().replace('_', '-')">
          {{ statusLabel(app.applicationStatus) }}
        </span>
      </div>

      <!-- Компания -->
      <section class="section">
        <h3 class="section-title">Компания</h3>
        <div class="info-grid">
          <div class="info-row">
            <span class="label">Название</span>
            <span class="value">{{ app.supplier?.companyName || app.companyName || '—' }}</span>
          </div>
          <div class="info-row">
            <span class="label">ИНН</span>
            <span class="value">{{ app.supplier?.inn || app.companyInn || '—' }}</span>
          </div>
          <div class="info-row">
            <span class="label">ОКПО</span>
            <span class="value">{{ app.supplier?.okpo || '—' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Рег. номер</span>
            <span class="value">{{ app.supplier?.registrationNumber || '—' }}</span>
          </div>
          <div class="info-row">
            <span class="label">Дата регистрации</span>
            <span class="value">{{ app.supplier?.registrationDate || '—' }}</span>
          </div>
          @if (app.supplier?.legalStatus) {
          <div class="info-row">
            <span class="label">Правовой статус</span>
            <span class="value">{{ app.supplier!.legalStatus!.nameRu }}</span>
          </div>
          }
          <div class="info-row">
            <span class="label">Тип заявителя</span>
            <span class="value">{{ applicantTypeLabel(app.supplier?.applicantTypeEnum) }}</span>
          </div>
          <div class="info-row">
            <span class="label">Действует как</span>
            <span class="value">{{ actingAsLabel(app.supplier?.actingAs) }}</span>
          </div>
        </div>
      </section>

      <!-- Квота -->
      <section class="section">
        <h3 class="section-title">Квота</h3>
        <div class="info-grid">
          <div class="info-row">
            <span class="label">Вид мяса</span>
            <span class="value">{{ app.meatType.nameRu }}</span>
          </div>
          <div class="info-row">
            <span class="label">Запрошено</span>
            <span class="value">{{ toTons(app.requestedVolume) }} тонн</span>
          </div>
          @if (app.totalLimitVolume) {
          <div class="info-row">
            <span class="label">Лимит квоты</span>
            <span class="value">{{ toTons(app.totalLimitVolume) }} тонн</span>
          </div>
          }
        </div>
      </section>

      <!-- Причина отклонения / на рассмотрении -->
      @if (app.rejectionReason || app.underConsiderationReason) {
      <section class="section">
        <h3 class="section-title">Причина отказа</h3>
        <p class="note-text">{{ app.rejectionReason || app.underConsiderationReason }}</p>
      </section>
      }

    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-raised-button color="primary" (click)="close()">Закрыть</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .detail-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px 0;
    }
    .detail-header h2 {
      margin: 0 !important;
      font-size: 20px !important;
      font-weight: 600 !important;
    }
    .detail-content {
      padding: 16px 24px !important;
      min-width: 420px;
    }
    .status-row { margin-bottom: 16px; }
    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
    }
    .status-pending { background: #e0e0e0; color: #424242; }
    .status-approved { background: #c8e6c9; color: #2e7d32; }
    .status-rejected { background: #ffcdd2; color: #c62828; }
    .status-under-consideration { background: #b3e5fc; color: #0277bd; }

    .section { margin-bottom: 20px; }
    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #1d5b37;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin: 0 0 10px;
      padding-bottom: 6px;
      border-bottom: 2px solid #e8f5e9;
    }
    .info-grid { display: flex; flex-direction: column; gap: 8px; }
    .info-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      font-size: 14px;
    }
    .label { color: #888; flex-shrink: 0; }
    .value { color: #222; font-weight: 500; text-align: right; }
    .note-text { font-size: 14px; color: #555; margin: 0; line-height: 1.5; }

    @media (max-width: 480px) {
      .detail-header {
        padding: 12px 16px 0;
      }
      .detail-header h2 {
        font-size: 17px !important;
      }
      .detail-content {
        padding: 12px 16px 12px 16px !important;
        min-width: unset;
        box-sizing: border-box;
      }
      .info-row {
        flex-direction: column;
        gap: 2px;
      }
      .value {
        text-align: left;
      }
    }

    @media (max-width: 480px) {
      ::ng-deep .mat-mdc-dialog-content {
        padding-left: 16px !important;
        padding-right: 16px !important;
      }
      ::ng-deep .mat-mdc-dialog-surface {
        padding: 0 !important;
      }
    }
  `]
})
export class ApplicationDetailDialogComponent {
  app = inject<ApplicationItem>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<ApplicationDetailDialogComponent>);

  close(): void { this.dialogRef.close(); }

  toTons(kg: number): string {
    return (kg / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'На рассмотрении',
      APPROVED: 'Одобрено',
      REJECTED: 'Отклонено',
      UNDER_CONSIDERATION: 'На проверке'
    };
    return map[status] ?? status;
  }

  applicantTypeLabel(type?: string): string {
    if (type === 'LIMITED_LIABILITY_COMPANY') return 'ОсОО';
    if (type === 'INDIVIDUAL_ENTREPRENEUR') return 'ИП';
    return type ?? '—';
  }

  actingAsLabel(val?: string): string {
    if (val === 'DIRECTOR') return 'Директор';
    if (val === 'AGENT') return 'Агент';
    return val ?? '—';
  }
}
